import { useMutation } from "@tanstack/react-query";
import { getBlob, ref, StorageReference } from "firebase/storage";
import FrontHandIcon from "@mui/icons-material/FrontHand";
import BackHandIcon from "@mui/icons-material/BackHand";
import { functions, storage } from "../../../firebase";
import {
  ensureCorrectTimestamp,
  FullUser,
  SerializedTimestamp,
} from "../../../hooks/useCurrentUser";
import { Loading, PrimaryButton, SecondaryButton } from "../../../components";
import { httpsCallable } from "firebase/functions";

import "./legal_docs_approval.scss";
import { useCallback, useEffect, useState } from "react";
import { saveAs } from "file-saver";

const LegalDocsApprovalPage = () => {
  return (
    <section className="part-page">
      <div id="main-word">
        <h2>
          <span className="highlighted">Vérifier</span> des profils utilisateurs
        </h2>
        <PendingApprovalsList />
      </div>
    </section>
  );
};

interface PendingApprovalDBEntity {
  userId: string;
  documentType: "id" | "driver-licence";
  requestDate: SerializedTimestamp;
  id: string;
}

interface Approval {
  userId: string;
  userFirstname: string;
  userLastname: string;
  userBirthDate?: Date;
  documentType: "id" | "driver-licence";
  frontDocRef: StorageReference;
  backDocRef: StorageReference;
  requestDate: Date;
  id: string;
}

async function getPendingLegalsApprovals() {
  const callResult = await httpsCallable(
    functions,
    "users-getPendingLegalDocsApproval"
  )();
  const result = callResult.data as PendingApprovalDBEntity[] | undefined;

  if (!result) {
    return [];
  }

  const approvalRequests: Approval[] = [];

  for await (const req of result) {
    const userDataReq = await httpsCallable(
      functions,
      "users-getUserInfoForView"
    )({
      uuids: [req.userId],
      showAllData: true,
    });

    const userData = (userDataReq.data as FullUser[] | undefined)?.at(0);

    if (!userData) {
      continue;
    }

    const fileName = (suffix: string) => {
      return `${req.userId}_legal-${req.documentType}-${suffix}`;
    };

    const frontFileName = fileName("front");
    const backFileName = fileName("back");

    const frontFileRef = ref(storage, `${req.userId}/${frontFileName}`);
    const backFileRef = ref(storage, `${req.userId}/${backFileName}`);

    const userBirthDate = ensureCorrectTimestamp(userData.birthDate);

    approvalRequests.push({
      userId: req.userId,
      userFirstname: userData.firstname,
      userLastname: userData.lastname,
      userBirthDate: userBirthDate?.toDate(),
      documentType: req.documentType,
      frontDocRef: frontFileRef,
      backDocRef: backFileRef,
      requestDate: new Date(req.requestDate._seconds * 1000),
      id: req.id,
    });
  }

  return approvalRequests;
}

const PendingApprovalsList = () => {
  const [approvals, setApprovals] = useState<Approval[]>([]);

  const {
    mutate: getPendingApprovals,
    isPending,
    isError,
    error,
  } = useMutation({
    mutationKey: ["pending-legals-approvals"],
    mutationFn: async () => {
      const fetchedApprovals = await getPendingLegalsApprovals();
      setApprovals(fetchedApprovals);
    },
  });

  useEffect(() => {
    getPendingApprovals();
  }, [getPendingApprovals]);

  return (
    <>
      {isPending && <Loading />}
      {isError && (
        <p>
          Une erreur est survenue : {error.name} {error.message}
        </p>
      )}
      {!isPending && (
        <ul id="pending-approvals-list">
          {approvals.map((req) => (
            <PendingApprovalCard
              key={req.id}
              approval={req}
              onApprovedOrRejected={() => {
                setApprovals((prev) => {
                  const newApprovals = [...prev];
                  newApprovals.splice(
                    newApprovals.findIndex((obj) => obj.id === req.id),
                    1
                  );
                  return newApprovals;
                });
              }}
            />
          ))}
        </ul>
      )}
    </>
  );
};

const documentNames = {
  id: "Carte d'identité",
  "driver-licence": "Permis de conduire",
};

async function downloadFile(ref: StorageReference) {
  const blob = await getBlob(ref);
  const fileName = ref.name;
  saveAs(blob, fileName);
}

interface PendingApprovalCardProps {
  approval: Approval;
  onApprovedOrRejected: () => void;
}

const PendingApprovalCard = ({
  approval,
  onApprovedOrRejected,
}: PendingApprovalCardProps) => {
  const approve = useCallback(async () => {
    if (
      !window.confirm(
        `Veux-tu vraiment approuver la demande de ${
          approval.userFirstname
        } concernant son ${documentNames[approval.documentType]} ?`
      )
    ) {
      return;
    }

    await httpsCallable(
      functions,
      "users-approveOrRejectUserLegalDocument"
    )({
      userId: approval.userId,
      docType: approval.documentType,
      approved: true,
    });
    onApprovedOrRejected();
  }, [
    approval.userId,
    approval.userFirstname,
    approval.documentType,
    onApprovedOrRejected,
  ]);

  const refuse = useCallback(async () => {
    const reason = prompt("Transmettre une raison de refus");

    if (reason == null) {
      alert("Une raison est requise pour refuser la demande.");
      return;
    }

    await httpsCallable(
      functions,
      "users-approveOrRejectUserLegalDocument"
    )({
      userId: approval.userId,
      docType: approval.documentType,
      approved: false,
      cause: reason,
    });
    onApprovedOrRejected();
  }, [approval.userId, approval.documentType, onApprovedOrRejected]);

  return (
    <div className="pending-approval-card">
      <div id="overview">
        <div id="basic-infos">
          <p>{documentNames[approval.documentType]}</p>
          <p className="approval-date">
            {approval.requestDate.toLocaleString()}
          </p>
        </div>
        <p className="approval-id">{approval.id}</p>
      </div>
      <div id="infos-docs">
        <div className="user-infos">
          <p>
            {approval.userFirstname} {approval.userLastname.toLocaleUpperCase()}
          </p>
          <p>{approval.userBirthDate?.toLocaleDateString() ?? "NaN"}</p>
        </div>
        <div className="docs">
          <div
            className="doc-link"
            onClick={() => downloadFile(approval.frontDocRef)}
          >
            <FrontHandIcon />
            <p>Recto</p>
          </div>
          <div
            className="doc-link"
            onClick={() => downloadFile(approval.backDocRef)}
          >
            <BackHandIcon />
            <p>verso</p>
          </div>
        </div>
      </div>
      <div id="approval">
        <PrimaryButton onClick={approve}>Approuver</PrimaryButton>
        <SecondaryButton onClick={refuse}>Refuser</SecondaryButton>
      </div>
    </div>
  );
};

export default LegalDocsApprovalPage;
